/**
 * Pickup slot generation utilities for PickupTimePicker.
 * Pure functions — no React dependencies.
 */

import type { RestaurantHours, RestaurantHoursException } from '@/types/menuApi'
import {
  formatWallTime,
  getEffectiveBusinessTimeZone,
  getRelativeBusinessDate,
  getZonedDateParts,
  zonedWallTimeToUtcDate,
} from '@/utils/timezone'
import { timeToMinutes } from '@/utils/storeHours'

export interface PickupSlot {
  label: string
  scheduledPickupAt: string
}

export function minutesToTime24(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Generate 30-min pickup slots for the given business-local date using API hours */
export function generateSlots(
  dayOffset: 0 | 1,
  hours?: RestaurantHours[],
  hoursExceptions?: RestaurantHoursException[],
  timeZone?: string | null,
  lastOrderMinutes = 0,
  now = new Date()
): PickupSlot[] {
  if (!hours || hours.length === 0) return []

  const businessTimeZone = getEffectiveBusinessTimeZone(timeZone, undefined)
  const targetDate = getRelativeBusinessDate(businessTimeZone, dayOffset, now)
  const targetIsoDate = `${targetDate.year}-${String(targetDate.month).padStart(2, '0')}-${String(targetDate.day).padStart(2, '0')}`
  const dayException = hoursExceptions?.find((entry) => entry.date === targetIsoDate)

  if (dayException?.is_closed) return []

  const is24 = dayException
    ? dayException.is_24_hours
    : hours.find((entry) => entry.day_of_week === targetDate.weekday)?.is_24_hours

  const dayHours = dayException?.open_time && dayException?.close_time
    ? {
        open_time: dayException.open_time,
        close_time: dayException.close_time,
        is_closed: false,
        day_of_week: targetDate.weekday,
      }
    : hours.find((entry) => entry.day_of_week === targetDate.weekday)

  if (
    !is24 &&
    (!dayHours || dayHours.is_closed || !dayHours.open_time || !dayHours.close_time)
  ) {
    return []
  }

  const open = is24 ? 0 : timeToMinutes(dayHours!.open_time!)
  const close = is24 ? 24 * 60 : timeToMinutes(dayHours!.close_time!)
  const effectiveClose = lastOrderMinutes > 0 && !is24 ? close - lastOrderMinutes : close
  const isOvernight = !is24 && open > close

  let earliestMinute = -1
  if (dayOffset === 0) {
    const businessNow = getZonedDateParts(now, businessTimeZone)
    const currentMinutes = businessNow.hour * 60 + businessNow.minute
    earliestMinute = Math.ceil((currentMinutes + 30) / 30) * 30
  }

  const slots: PickupSlot[] = []

  if (isOvernight) {
    if (dayOffset === 0 && earliestMinute >= 0) {
      const businessNow = getZonedDateParts(now, businessTimeZone)
      const currentMinutes = businessNow.hour * 60 + businessNow.minute

      if (currentMinutes >= open) {
        const preStart = Math.max(open, earliestMinute)
        for (let m = preStart; m < 1440; m += 30) {
          const time24 = minutesToTime24(m)
          slots.push({
            label: formatWallTime(time24),
            scheduledPickupAt: zonedWallTimeToUtcDate(targetDate, time24, businessTimeZone).toISOString(),
          })
        }
        const nextDate = getRelativeBusinessDate(businessTimeZone, dayOffset + 1, now)
        for (let m = 0; m <= effectiveClose; m += 30) {
          const time24 = minutesToTime24(m)
          slots.push({
            label: formatWallTime(time24),
            scheduledPickupAt: zonedWallTimeToUtcDate(nextDate, time24, businessTimeZone).toISOString(),
          })
        }
      } else if (currentMinutes < close) {
        const postStart = Math.max(0, earliestMinute)
        for (let m = postStart; m <= effectiveClose; m += 30) {
          const time24 = minutesToTime24(m)
          slots.push({
            label: formatWallTime(time24),
            scheduledPickupAt: zonedWallTimeToUtcDate(targetDate, time24, businessTimeZone).toISOString(),
          })
        }
      }
    } else {
      for (let m = open; m < 1440; m += 30) {
        const time24 = minutesToTime24(m)
        slots.push({
          label: formatWallTime(time24),
          scheduledPickupAt: zonedWallTimeToUtcDate(targetDate, time24, businessTimeZone).toISOString(),
        })
      }
      const nextDate = getRelativeBusinessDate(businessTimeZone, dayOffset + 1, now)
      for (let m = 0; m <= effectiveClose; m += 30) {
        const time24 = minutesToTime24(m)
        slots.push({
          label: formatWallTime(time24),
          scheduledPickupAt: zonedWallTimeToUtcDate(nextDate, time24, businessTimeZone).toISOString(),
        })
      }
    }
  } else {
    const startAt = dayOffset === 0 ? Math.max(open, earliestMinute) : open
    for (let m = startAt; m <= effectiveClose; m += 30) {
      const time24 = minutesToTime24(m)
      slots.push({
        label: formatWallTime(time24),
        scheduledPickupAt: zonedWallTimeToUtcDate(targetDate, time24, businessTimeZone).toISOString(),
      })
    }
  }

  return slots
}
